/**
 * Accessibility Helpers
 * 
 * Reusable utilities for implementing accessibility features
 * across chess components. Based on existing patterns from MoveHistoryNavigator.tsx
 * and other accessible components.
 */

export interface AriaLabelConfig {
  /** Base label for the element */
  label: string;
  /** Additional descriptive text */
  description?: string;
  /** Current state information */
  state?: string;
  /** Position information (e.g., "3 of 8") */
  position?: string;
}

export interface FocusManagementConfig {
  /** Whether to trap focus within the container */
  trapFocus?: boolean;
  /** Element to focus when container is activated */
  initialFocus?: HTMLElement | string;
  /** Element to return focus to when container is deactivated */
  returnFocus?: HTMLElement;
  /** Whether to restore focus on unmount */
  restoreFocus?: boolean;
}

/**
 * Create comprehensive ARIA label from configuration
 * @param config ARIA label configuration
 * @returns Complete ARIA label string
 */
export const createAriaLabel = (config: AriaLabelConfig): string => {
  const parts = [config.label];
  
  if (config.state) {
    parts.push(config.state);
  }
  
  if (config.position) {
    parts.push(config.position);
  }
  
  if (config.description) {
    parts.push(config.description);
  }
  
  return parts.join(', ');
};

/**
 * Create ARIA attributes for chess squares
 * @param square Chess square notation (e.g., 'e4')
 * @param piece Piece on the square (optional)
 * @param isSelected Whether the square is selected
 * @param isHighlighted Whether the square is highlighted
 * @param isLegalMove Whether the square is a legal move destination
 * @returns Object with ARIA attributes
 */
export const createSquareAriaAttributes = (
  square: string,
  piece?: { type: string; color: string } | null,
  isSelected = false,
  isHighlighted = false,
  isLegalMove = false
) => {
  const parts = [`Square ${square}`];
  
  if (piece) {
    const colorName = piece.color === 'w' ? 'white' : 'black';
    const pieceName = getPieceDisplayName(piece.type);
    parts.push(`${colorName} ${pieceName}`);
  } else {
    parts.push('empty');
  }
  
  const states = [];
  if (isSelected) states.push('selected');
  if (isHighlighted) states.push('highlighted');
  if (isLegalMove) states.push('legal move');
  
  if (states.length > 0) {
    parts.push(states.join(', '));
  }
  
  return {
    'aria-label': parts.join(', '),
    'aria-selected': isSelected,
    'aria-current': isSelected ? ('true' as const) : undefined,
    'role': 'gridcell',
    'tabIndex': isSelected ? 0 : -1
  };
};

/**
 * Get display name for chess piece type
 * @param pieceType Single character piece type
 * @returns Human-readable piece name
 */
export const getPieceDisplayName = (pieceType: string): string => {
  const pieceNames: Record<string, string> = {
    'p': 'pawn',
    'r': 'rook',
    'n': 'knight',
    'b': 'bishop',
    'q': 'queen',
    'k': 'king'
  };
  
  return pieceNames[pieceType.toLowerCase()] || 'piece';
};

/**
 * Create ARIA attributes for navigation buttons
 * @param action The action the button performs
 * @param isDisabled Whether the button is disabled
 * @param position Optional position information
 * @returns Object with ARIA attributes
 */
export const createNavigationAriaAttributes = (
  action: string,
  isDisabled = false,
  position?: string
) => {
  const label = position ? `${action} (${position})` : action;
  
  return {
    'aria-label': label,
    'aria-disabled': isDisabled,
    'disabled': isDisabled
  };
};

/**
 * Manage focus within a container
 * @param container The container element
 * @param config Focus management configuration
 * @returns Cleanup function
 */
export const manageFocus = (
  container: HTMLElement,
  config: FocusManagementConfig = {}
): (() => void) => {
  const { trapFocus = false, initialFocus, returnFocus, restoreFocus = true } = config;
  
  // Store the previously focused element
  const previouslyFocused = document.activeElement as HTMLElement;
  
  // Focus initial element
  if (initialFocus) {
    const elementToFocus = typeof initialFocus === 'string' 
      ? container.querySelector(initialFocus) as HTMLElement
      : initialFocus;
    
    if (elementToFocus) {
      elementToFocus.focus();
    }
  }
  
  // Focus trap handler
  const handleKeyDown = (event: KeyboardEvent) => {
    if (!trapFocus || event.key !== 'Tab') return;
    
    const focusableElements = container.querySelectorAll(
      'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
    ) as NodeListOf<HTMLElement>;
    
    if (focusableElements.length === 0) return;
    
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    
    if (event.shiftKey) {
      // Shift + Tab
      if (document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      }
    } else {
      // Tab
      if (document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }
  };
  
  if (trapFocus) {
    container.addEventListener('keydown', handleKeyDown);
  }
  
  // Cleanup function
  return () => {
    if (trapFocus) {
      container.removeEventListener('keydown', handleKeyDown);
    }
    
    // Restore focus
    if (restoreFocus) {
      const elementToFocus = returnFocus || previouslyFocused;
      if (elementToFocus && elementToFocus.focus) {
        elementToFocus.focus();
      }
    }
  };
};

/**
 * Create live region for announcing changes
 * @param message Message to announce
 * @param priority Priority level ('polite' or 'assertive')
 * @param timeout How long to keep the message (ms)
 */
export const announceToScreenReader = (
  message: string,
  priority: 'polite' | 'assertive' = 'polite',
  timeout = 1000
): void => {
  // Find or create live region
  let liveRegion = document.getElementById('chess-live-region') as HTMLElement;
  
  if (!liveRegion) {
    liveRegion = document.createElement('div');
    liveRegion.id = 'chess-live-region';
    liveRegion.setAttribute('aria-live', priority);
    liveRegion.setAttribute('aria-atomic', 'true');
    liveRegion.style.position = 'absolute';
    liveRegion.style.left = '-10000px';
    liveRegion.style.width = '1px';
    liveRegion.style.height = '1px';
    liveRegion.style.overflow = 'hidden';
    document.body.appendChild(liveRegion);
  }
  
  // Update the live region
  liveRegion.textContent = message;
  
  // Clear the message after timeout
  setTimeout(() => {
    if (liveRegion.textContent === message) {
      liveRegion.textContent = '';
    }
  }, timeout);
};

/**
 * Create keyboard shortcut tooltip text
 * @param shortcuts Array of keyboard shortcuts
 * @param action Description of the action
 * @returns Formatted tooltip text
 */
export const createKeyboardShortcutTooltip = (
  shortcuts: string[],
  action: string
): string => {
  const shortcutText = shortcuts.join(' or ');
  return `${action} (${shortcutText})`;
};

/**
 * Check if an element is visible and focusable
 * @param element The element to check
 * @returns Whether the element is focusable
 */
export const isFocusable = (element: HTMLElement): boolean => {
  // Check if element is disabled
  if (element.hasAttribute('disabled') || element.getAttribute('aria-disabled') === 'true') {
    return false;
  }
  
  // Check if element has negative tabindex
  const tabIndex = element.getAttribute('tabindex');
  if (tabIndex === '-1') {
    return false;
  }
  
  // Check if element is visible
  const style = window.getComputedStyle(element);
  if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
    return false;
  }
  
  // Check if element has zero dimensions
  const rect = element.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) {
    return false;
  }
  
  return true;
};

/**
 * Get the next focusable element in a container
 * @param container The container to search within
 * @param currentElement The currently focused element
 * @param direction Direction to search ('forward' or 'backward')
 * @returns Next focusable element or null
 */
export const getNextFocusableElement = (
  container: HTMLElement,
  currentElement: HTMLElement,
  direction: 'forward' | 'backward' = 'forward'
): HTMLElement | null => {
  const focusableElements = Array.from(
    container.querySelectorAll(
      'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
    )
  ).filter(el => isFocusable(el as HTMLElement)) as HTMLElement[];
  
  if (focusableElements.length === 0) return null;
  
  const currentIndex = focusableElements.indexOf(currentElement);
  
  if (direction === 'forward') {
    return focusableElements[currentIndex + 1] || focusableElements[0];
  } else {
    return focusableElements[currentIndex - 1] || focusableElements[focusableElements.length - 1];
  }
};

/**
 * Create ARIA attributes for a chess board grid
 * @param rows Number of rows
 * @param cols Number of columns
 * @returns Object with ARIA attributes for the grid container
 */
export const createChessBoardAriaAttributes = (rows = 8, cols = 8) => {
  return {
    'role': 'grid',
    'aria-label': `Chess board, ${rows} by ${cols} grid`,
    'aria-rowcount': rows,
    'aria-colcount': cols
  };
};

/**
 * Create ARIA attributes for sequence challenge feedback
 * @param isCorrect Whether the move was correct
 * @param moveNumber Current move number
 * @param totalMoves Total moves in sequence
 * @param feedback Feedback message
 * @returns Object with ARIA attributes
 */
export const createSequenceFeedbackAriaAttributes = (
  isCorrect: boolean,
  moveNumber: number,
  totalMoves: number,
  feedback?: string
) => {
  const status = isCorrect ? 'correct' : 'incorrect';
  const position = `Move ${moveNumber} of ${totalMoves}`;
  const message = feedback || `Move is ${status}`;
  
  // Remove trailing punctuation from message to avoid double periods
  const cleanMessage = message.replace(/[.!?]+$/, '');
  
  return {
    'role': 'status',
    'aria-live': 'polite',
    'aria-label': `${cleanMessage}. ${position}`,
    'aria-atomic': 'true'
  };
}; 